import type { IBuildTimeSeriesQueryParams } from './clauses/time-series-query';
import type { IBuildConditionQueryParams } from './clauses/condition-query';
import type { IBuildTimezoneQueryParams } from './clauses/timezone-query';
import type { IBuildOrderByQueryParams } from './clauses/order-by-query';
import type { IBuildOffsetQueryParams } from './clauses/offset-query';
import type { IBuildGroupQueryParams } from './clauses/group-query';
import type { IBuildLimitQueryParams } from './clauses/limit-query';
import type { IBuildModeQueryParams } from './clauses/mode-query';
import type { IBuildFromQueryParams } from './clauses/from-query';
import type { IBuildShowQueryParams } from './clauses/show-query';
import type { IBuildTimeQueryParams } from './clauses/time-query';

import { getFirstMetricInOrderBy } from './filters/build-filter-conditions';
import { isValidQueryInput } from './helpers/is-valid-query-input';
import { buildTimeSeriesQuery } from './clauses/time-series-query';
import { buildConditionQuery } from './clauses/condition-query';
import { buildTimezoneQuery } from './clauses/timezone-query';
import { buildOrderByQuery } from './clauses/order-by-query';
import { buildOffsetQuery } from './clauses/offset-query';
import { buildGroupQuery } from './clauses/group-query';
import { buildLimitQuery } from './clauses/limit-query';
import { buildModeQuery } from './clauses/mode-query';
import { buildFromQuery } from './clauses/from-query';
import { buildShowQuery } from './clauses/show-query';
import { buildTimeQuery } from './clauses/time-query';
import { DEFAULT_QUERY_LIMIT } from './configs';

export type IBuildGemXQlQueryParams = IBuildModeQueryParams &
  IBuildFromQueryParams &
  IBuildShowQueryParams &
  IBuildGroupQueryParams &
  IBuildTimeQueryParams &
  IBuildTimeSeriesQueryParams &
  IBuildTimezoneQueryParams &
  IBuildOrderByQueryParams &
  IBuildConditionQueryParams &
  IBuildLimitQueryParams &
  IBuildOffsetQueryParams & {
    /** Fields the server supports in ORDER BY without requiring them in SELECT/GROUP BY. */
    extraOrderByFields?: string[];
  };

export const buildGemXQlQuery = (params: IBuildGemXQlQueryParams) => {
  const {
    mode,
    metrics,
    dimensions,
    dateRange,
    compareDateRange,
    filters,
    overrideFilters,
    versionIds,
    extraConditions,
    orderBy,
    sources,
    timeDimension,
    groupWithClause,
    timezone,
    limit = DEFAULT_QUERY_LIMIT,
    offset,
    extraOrderByFields,
  } = params;
  const columns = [
    ...dimensions,
    ...getFirstMetricInOrderBy(orderBy, metrics),
    ...(extraOrderByFields ?? []),
  ];

  const builtQuery = [
    buildModeQuery(mode),
    buildFromQuery(sources),
    buildShowQuery(metrics),
    buildGroupQuery(dimensions, groupWithClause),
    buildTimeSeriesQuery(timeDimension),
    buildTimeQuery({ dateRange, compareDateRange, timeDimension, overrideFilters }),
    buildTimezoneQuery(timezone),
    buildConditionQuery({ filters, overrideFilters, versionIds, extraConditions }),
    buildOrderByQuery(orderBy, columns),
    buildLimitQuery(limit),
    buildOffsetQuery(offset),
  ]
    .filter(Boolean)
    .join(' ');

  return isValidQueryInput(builtQuery) ? builtQuery : '';
};
